import { useCallback, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Flex } from '@molecule/app-ui-react'

import { formatResetCountdown } from '../upgrade-copy.js'
import {
  formatCents,
  LIMIT_STATE_COLOR,
  type LimitState,
  nextUtcDailyResetMs,
  type ProjectUsageView,
  projectUsageView,
  type UsageResponse,
} from '../utilities/usage-utilities.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'

/**
 * Refresh cadence (ms) while the Usage view is open.
 *
 * The Activity panel pauses its own poll on this view "because Usage has its
 * own polling" — which was not true: this component fetched once on mount and
 * then sat frozen, so a user watching spend during a build saw a number that
 * only moved if they closed and reopened the panel. Slower than the Activity
 * poll on purpose: a usage rollup is an aggregate query, not a tail.
 */
export const DEFAULT_USAGE_POLL_MS = 15_000

/**
 * Props for the per-project Usage inspector.
 */
interface UsageInspectorProps {
  /** Project whose brokered-service usage to surface. */
  projectId: string
  /**
   * Refresh cadence in ms. Defaults to {@link DEFAULT_USAGE_POLL_MS}; `0`
   * disables the poll (used by tests that assert a single fetch).
   */
  pollIntervalMs?: number
}

/**
 * A single brokered-service usage row: provider · category, with requests +
 * units (and billed cost when the line carried one), plus a relative bar.
 * @param root0 - Component props.
 * @param root0.row - The shaped usage row.
 * @param root0.maxRequests - The busiest row's request count (bar denominator).
 * @returns The rendered row element.
 */
function UsageRow({
  row,
  maxRequests,
}: {
  row: ProjectUsageView['rows'][number]
  maxRequests: number
}) {
  const { t } = useTranslation()
  const pct = maxRequests > 0 ? Math.min((row.requests / maxRequests) * 100, 100) : 0
  return (
    <div
      style={{ marginBottom: 12 }}
      data-mol-id={`usage-inspector-row-${row.provider}-${row.category}`}
    >
      <Flex align="center" justify="between" style={{ fontSize: 13, marginBottom: 4, gap: 8 }}>
        <span
          style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {row.label}
        </span>
        <span
          style={{
            color: 'var(--mol-color-text-secondary, #aaaaaa)',
            flexShrink: 0,
            textAlign: 'right',
          }}
        >
          {t(
            'usageInspector.line',
            { requests: row.requests, units: row.units },
            { defaultValue: '{{requests}} req · {{units}} units' },
          )}
          {row.costCents != null && (
            <span style={{ fontWeight: 600, marginLeft: 6 }}>{formatCents(row.costCents)}</span>
          )}
        </span>
      </Flex>
      <div
        style={{
          height: 6,
          background: 'var(--color-surface)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: 'var(--color-primary-light, #4070e0)',
            borderRadius: 3,
          }}
        />
      </div>
    </div>
  )
}

/**
 * Per-project Usage inspector — surfaces a deployed project's brokered-service
 * spend from `GET /projects/:id/usage`: a per-(provider, category) breakdown
 * (requests + tokens/units, and billed cost when the rollup carries one), the
 * billing period, and today's AI spend vs the daily budget.
 *
 * Mirrors the account-wide Billing page's brokered-usage presentation but for a
 * single project, and lives as a tab inside the {@link ActivityPanel} so dev
 * side-effects and production service usage share one surface (per
 * `docs/BAAS-activity.md` §5 — the AI activity inspector is the same surface).
 *
 * All shaping/formatting lives in `utilities/usage-utilities.ts`; this is the
 * thin presentational shell.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose brokered usage to surface.
 * @returns The rendered Usage inspector element.
 */
export function UsageInspector({
  projectId,
  pollIntervalMs = DEFAULT_USAGE_POLL_MS,
}: UsageInspectorProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const [view, setView] = useState<ProjectUsageView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchUsage = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      // A background refresh keeps the numbers on screen; only the first load
      // (and an explicit retry) may replace them with the spinner.
      if (!silent) {
        setLoading(true)
        setError(false)
      }
      try {
        const res = await http.get<UsageResponse>(`/projects/${projectId}/usage`)
        setView(projectUsageView(res.data ?? null))
        if (silent) setError(false)
      } catch (error) {
        console.error('UsageInspector: failed to fetch usage', error)
        // A failed background refresh must not blank a view that is already
        // rendering real numbers — only surface it when there is nothing else.
        if (!silent) setError(true)
      }
      if (!silent) setLoading(false)
    },
    [http, projectId],
  )

  useEffect(() => {
    void fetchUsage()
  }, [fetchUsage])

  // Keep it live. Paused while the tab is hidden, like the Activity poll.
  useEffect(() => {
    if (pollIntervalMs <= 0) return
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      void fetchUsage({ silent: true })
    }, pollIntervalMs)
    return () => clearInterval(id)
  }, [pollIntervalMs, fetchUsage])

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        className={cm.sp('py', 6)}
        data-mol-id="usage-inspector"
      >
        <MoleculeSpinner />
      </Flex>
    )
  }

  if (error || !view) {
    return (
      <div
        style={{ textAlign: 'center', padding: 16, fontSize: 13 }}
        data-mol-id="usage-inspector-error"
      >
        <p style={{ color: 'var(--mol-color-error, #ef4444)', marginBottom: 10 }}>
          {t('usageInspector.loadError', undefined, {
            defaultValue: 'Failed to load usage data.',
          })}
        </p>
        {/* Retry — solid primary, the canonical recovery CTA (DESIGN.md); `xs`
            because this one sits inside a dense panel, not on a page. */}
        <button
          type="button"
          data-mol-id="usage-inspector-retry"
          onClick={() => void fetchUsage()}
          className={cm.cn(
            cm.button({ variant: 'solid', color: 'primary', size: 'xs' }),
            cm.touchTargetCompact,
          )}
        >
          {t('common.retry', undefined, { defaultValue: 'Retry' })}
        </button>
      </div>
    )
  }

  const maxRequests = view.rows.reduce((max, r) => Math.max(max, r.requests), 1)
  const aiColor: string = LIMIT_STATE_COLOR[view.aiCostState as LimitState]

  return (
    <div data-mol-id="usage-inspector">
      {/* Period + today's AI spend vs daily budget */}
      <div
        data-mol-id="usage-inspector-summary"
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          padding: 12,
          marginBottom: 14,
        }}
      >
        {view.period && (
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>
            {t('usageInspector.period', undefined, { defaultValue: 'Period' })}:{' '}
            {new Date(view.period.start).toLocaleDateString(undefined, { timeZone: 'UTC' })} {'–'}{' '}
            {new Date(view.period.end).toLocaleDateString(undefined, { timeZone: 'UTC' })}
          </div>
        )}
        <Flex align="center" justify="between" style={{ fontSize: 13, marginBottom: 4, gap: 8 }}>
          <span style={{ fontWeight: 600 }}>
            {/*
              The heading names the window the percent below it is measured
              against. "AI usage today" over a MONTHLY meter reads as a daily
              figure, so a Pro user at 62% of their month saw what looked like
              62% of one day.
            */}
            {view.aiWindow === 'billing-period'
              ? t('usageInspector.aiUsageThisMonth', undefined, {
                  defaultValue: 'AI usage this month',
                })
              : t('usageInspector.aiUsageToday', undefined, { defaultValue: 'AI usage today' })}
          </span>
          <span style={{ color: aiColor, fontWeight: view.aiCostState === 'ok' ? 400 : 600 }}>
            {view.aiCostState === 'unlimited'
              ? t('usageInspector.aiUsageUnlimited', undefined, { defaultValue: 'Unlimited' })
              : t(
                  'usageInspector.aiUsagePercent',
                  { percent: view.aiUsedPercent },
                  { defaultValue: '{{percent}}% used' },
                )}
          </span>
        </Flex>
        <div
          style={{
            height: 6,
            background: 'var(--color-surface)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${view.aiUsedPercent}%`,
              background: aiColor,
              borderRadius: 3,
              transition: 'width 0.3s',
            }}
          />
        </div>
        {/*
          CST4: the AI budget is a retention lever, not a paywall — without a
          reset hint a user at "$0.60 / $0.60" reads a hard wall. Surface WHEN the
          allowance refreshes ("in about Xh" / "in about 12 days"), matching the
          toast / in-chat card / nudge surfaces and reusing the same countdown
          helper. Only for a real bounded cap — an unlimited budget never resets,
          so the hint would be misleading there.

          The LABEL names the tier's actual window (`aiWindow`): only the unpaid
          tiers reset daily, and calling a paid tier's monthly allowance "Daily"
          made the plan look day-capped. The UTC-midnight fallback is likewise
          daily-only — there is nothing sensible to guess for a billing period,
          so with no window end that line simply doesn't render.
        */}
        {view.aiCostState !== 'unlimited' &&
          view.aiCostCentsLimit > 0 &&
          (view.aiResetsAt || view.aiWindow !== 'billing-period') && (
            <div
              data-mol-id="usage-inspector-ai-reset"
              style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}
            >
              {(() => {
                // Prefer the server's exact window end; fall back to the
                // UTC-midnight approximation only for a daily window.
                const when = formatResetCountdown(
                  view.aiResetsAt ? new Date(view.aiResetsAt).getTime() : nextUtcDailyResetMs(),
                )
                return view.aiWindow === 'billing-period'
                  ? t(
                      'usageInspector.aiBudgetRefreshesMonthly',
                      { when },
                      { defaultValue: 'Monthly allowance — refreshes {{when}}' },
                    )
                  : t(
                      'usageInspector.aiBudgetRefreshes',
                      { when },
                      { defaultValue: 'Daily allowance — refreshes {{when}}' },
                    )
              })()}
            </div>
          )}
      </div>

      {/* Brokered service usage by provider/category */}
      <Flex align="center" justify="between" style={{ marginBottom: 10 }}>
        <div className={cm.cn(cm.fontWeight('bold'))} style={{ fontSize: 13 }}>
          {t('usageInspector.brokeredUsage', undefined, { defaultValue: 'Service usage' })}
        </div>
        {!view.empty && (
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            {view.totalCostCents != null
              ? t(
                  'usageInspector.totalsWithCost',
                  {
                    requests: view.totalRequests,
                    units: view.totalUnits,
                    cost: formatCents(view.totalCostCents),
                  },
                  { defaultValue: '{{requests}} requests · {{units}} units · {{cost}}' },
                )
              : t(
                  'usageInspector.totals',
                  { requests: view.totalRequests, units: view.totalUnits },
                  { defaultValue: '{{requests}} requests · {{units}} units' },
                )}
          </div>
        )}
      </Flex>

      {view.empty ? (
        <div
          style={{ textAlign: 'center', padding: 16, fontSize: 13, opacity: 0.6 }}
          data-mol-id="usage-inspector-empty"
        >
          {t('usageInspector.empty', undefined, {
            defaultValue: 'No service usage this period.',
          })}
        </div>
      ) : (
        <div data-mol-id="usage-inspector-list">
          {view.rows.map((row) => (
            <UsageRow key={`${row.provider}.${row.category}`} row={row} maxRequests={maxRequests} />
          ))}
        </div>
      )}
    </div>
  )
}
